<template>
    <div class="container">
        <div class="grid grid-cols-4 gap-10 tablet:gap-6 mobile:block">
            <div class="mobile:hidden">
                <aside class="pt-10 space-y-6 sticky top-0">
                    <icon-input type="search" icon="fa-search" :placeholder="translate('zoek_software_placeholder')" color="grey" v-model="searchTerm"></icon-input>
                    
                    <div>
                        <h2 class="mb-2 font-medium text-md">{{ translate('filter_uitgelicht') }}</h2>
                        <div class="flex flex-col items-start gap-y-2">
                            <div class="flex items-center gap-3">
                                <input id="isNew" type="checkbox" class="!m-0" v-model="filters.isNew">
                                <label for="isNew" class="select-none text-sm mb-0">{{ translate('filter_recent') }}</label>
                            </div>
                            <div class="flex items-center gap-3">
                                <input id="isUpdated" type="checkbox" class="!m-0" v-model="filters.isUpdated">
                                <label for="isUpdated" class="select-none text-sm mb-0">{{ translate('filter_updated') }}</label>
                            </div>
                            <div class="flex items-center gap-3">
                                <input id="isSoonAvailable" type="checkbox" class="!m-0" v-model="filters.isSoonAvailable">
                                <label for="isSoonAvailable" class="select-none text-sm mb-0">{{ translate('filter_coming_soon') }}</label>
                            </div>
                        </div>
                    </div>
                    
                    <div>
                        <h2 class="mb-2 font-medium text-md">{{ translate('filter_type') }}</h2>
                        <div class="flex flex-col items-start gap-y-2">
                            <div class="flex items-center gap-3">
                                <input id="isERP" type="checkbox" class="!m-0" v-model="filters.isERP">
                                <label for="isERP" class="select-none text-sm mb-0">{{ translate('filter_erp') }}</label>
                            </div>
                            <div class="flex items-center gap-3">
                                <input id="isCRM" type="checkbox" class="!m-0" v-model="filters.isCRM">
                                <label for="isCRM" class="select-none text-sm mb-0">{{ translate('filter_crm') }}</label>
                            </div>
                            <div class="flex items-center gap-3">
                                <input id="isBoekhoudpakket" type="checkbox" class="!m-0" v-model="filters.isBoekhoudpakket">
                                <label for="isBoekhoudpakket" class="select-none text-sm mb-0">{{ translate('filter_boekhoudpakket') }}</label>
                            </div>
                        </div>
                    </div>
                    
                    <div>
                        <h2 class="mb-2 font-medium text-md">{{ translate('filter_feature') }}</h2>
                        <div class="flex flex-col items-start gap-y-2">
                            <div class="flex items-center gap-3">
                                <input id="hasPaymentExperience" type="checkbox" class="!m-0" v-model="filters.hasPaymentExperience">
                                <label for="hasPaymentExperience" class="select-none text-sm mb-0">{{ translate('filter_betalingservaring') }}</label>
                            </div>
                            <div class="flex items-center gap-3">
                                <input id="hasALerts" type="checkbox" class="!m-0" v-model="filters.hasAlerts">
                                <label for="hasALerts" class="select-none text-sm mb-0">{{ translate('filter_alerts') }}</label>
                            </div>
                        </div>
                    </div>
                    
                    <div>
                        <div class="p-6 mt-10 rounded-lg border border-grey-200 bg-grey-100/50 tablet:p-4">
                            <h2 class="text-base font-medium mb-3 tablet:text-sm">{{ translate('card_build_title') }}</h2>
                            <p class="text-sm tablet:text-xs">
                                {{ translate('card_build_description') }}
                            </p>
                            <a :href="'/' + language + '/integrations/build'" class="text-sm link mt-3 inline-block tablet:text-xs">{{ translate('card_build_more') }}<i class="far fa-xs fa-arrow-right ml-2"></i></a>
                        </div>
                    </div>
                </aside>
            </div>
            <div class="col-span-3 border-l border-grey-300 pl-10 pb-10 mobile:px-0 mobile:container mobile:border-l-0">
                <div v-if="filteredIntegrations.length > 0" class="mt-10 grid grid-cols-4 tablet:grid-cols-3 gap-6 tablet:gap-4 mobile:grid-cols-2 mobile:gap-2">
                    <integration-tile v-for="integration in filteredIntegrations" :integration="integration" :key="integration.link" :no-label="hideLabels"></integration-tile>
                </div>
                <div v-else class="mt-10">
                    <p class=" text-grey-700 italic">{{ translate('geen_pakketten_gevonden')}}</p>
                </div>
                <new-software></new-software>
            </div>
        </div>

     
    </div>
</template>

<script lang="ts">
import {defineComponent} from 'vue';
import IntegrationTile from "./components/tile.vue";
import IconInput from "../../components/icon-input.vue";
import {Integration, SoftwareLabel, SoftwareType} from "./types";
import QueryString from "../../lib/QueryString";
import {Language} from "../../types/dto/Language";
import Ajax from "../../lib/Ajax";
import NewSoftware from "./components/new-software.vue";

export default defineComponent({
    name: "marketplace",
    props:{
        hideLabels: Boolean 
    },
    components: {NewSoftware, IconInput, IntegrationTile},
    data() {
        return {
            searchTerm: '',
            filters: {
                isNew: false,  
                isUpdated: false,  
                isSoonAvailable: false,  
                isERP: false,  
                isCRM: false,  
                isBoekhoudpakket: false,  
                hasPaymentExperience: false,  
                hasAlerts: false
            } as {[key: string]: boolean},
            integrations: (window.cw.integrations || []) as Integration[],
            language: window.cw.language as Language,
            SoftwareLabel,
            SoftwareType
        }
    },
    computed: {
        hasFiltered(): boolean {
            return Object.keys(this.filters).some((k: string) => this.filters[k]) || !!this.searchTerm;
        },
        filteredIntegrations(): Integration[] {
            let filtered = this.integrations;
            
            if(this.hasFiltered) {
                filtered = filtered.filter(i => {
                    const matchesName = i.name.contains(this.searchTerm);
                    const matchesAnyKeyWord = i.keyWords.some(keyWord => keyWord.contains(this.searchTerm));
                    
                    const matchesLabel = (this.filters.isNew && i.label === SoftwareLabel.RECENT) ||
                        (this.filters.isUpdated && i.label === SoftwareLabel.UPDATED) || 
                        (this.filters.isSoonAvailable && i.label === SoftwareLabel.COMINGSOON) || 
                        (!this.filters.isNew && !this.filters.isUpdated && !this.filters.isSoonAvailable);
                    
                    const matchesType = (this.filters.isERP && i.isTypeErp) ||
                        (this.filters.isCRM && i.isTypeCrm) ||
                        (this.filters.isBoekhoudpakket && i.isTypeBoekhoudpakket) || 
                        (!this.filters.isERP && !this.filters.isCRM && !this.filters.isBoekhoudpakket);
                    
                    const matchesFeature = (this.filters.hasPaymentExperience && i.hasPaymentExperience) ||
                        (this.filters.hasAlerts && i.hasFollowUp) ||
                        (!this.filters.hasAlerts && !this.filters.hasPaymentExperience);
    
                    return (matchesName || matchesAnyKeyWord) && matchesLabel && matchesType && matchesFeature;
                });
            }

            return filtered.sort((a, b) => a.isActivated ? -1 : 1);
        }
    },
    mounted() {
        const featureFilter = QueryString.getUrlParameter("featureFilter");
        if(featureFilter === "be")
            this.filters.hasPaymentExperience = true;
    }
})
</script>